import { nextIcon, playIcon, pauseIcon, muteIcon, unmuteIcon, fullscreenIcon, exitFullscreenIcon, heartIconFilled, shareIcon, checkmark, closeIcon, heartIconOutlined, arrowUpIcon } from "./components/Icons";
import { ISource, IVideo, IListing } from "./WidgetTvTypes";
import { observeCustomElement } from "./utils/observe";
import { fixedShuffle, jaccardSort } from "./utils/shuffle";
// import "./ShortsStyles.css"
import { loadScript } from "./utils/loaders";
import { generateAds, appendViewerHeaders } from "./AdStack";
import { ICustomizations } from "./providers/types";
import { IEventAttributes, pushEvent, pushFirstAdViewed } from "./Events";
import { updateSources } from "./utils/hls";
import { isMobile } from "./utils/mobile";
import { makeCustomizationController, getAttributeAnyCasing } from "./providers/Customizations";
// import { iheartPlayer } from "./providers/iheartPlayer";
import { makeLikeProvider } from "./providers/likeProvider";
import { SellwildVideoElement } from "./VideoIMA";

interface SellwildShortsElement extends HTMLElement {
  fullscreenOpen: boolean
  playVideo: (index: number) => void
  pauseVideo: () => void
  closeFullscreen: (ev: Event) => void
}
interface HTMLLoadableElement extends HTMLElement {
  showLoader: () => HTMLElement
  hideLoader: () => HTMLElement
  preventScroll: () => HTMLElement
  loadThenPlay: () => void
}

interface HTMLLoadableDeferElement extends HTMLLoadableElement {
  loadThenPlayDefered: (t?: number) => void
  loadThenPlayDeferedTimer: number
}

interface HTMLVideoElementWithHls extends HTMLVideoElement {
  hls: any
  cleanupHls: (from?: string) => void
}

const { SLUG, NAME, TYPE, IU, IU_INSTREAM, CODE, AD_UNIT, VIDEOS, VIDEOS_LOCAL, VIDEO_TAG_CACHE_COUNT, TITLE, LINK_TEXT, LINK_HREF, FILTER, AD_TYPE, SCHAIN_SID, S2S_CONFIG, AD_REFRESH_MAX, AD_REFRESH_INTERVAL, AD_DISABLE_DISPLAY, BANNER_ZID, BOTTOM_BANNER_ZID, MOBILE_BANNER_ZID, MOBILE_ZID, DISPLAY_ZID, BIDSTREAM_STACK, MAX_DISPLAY_ADS, CSS, INITIAL, PRIMARY_COLOR, AUDIGENT, FONT_FAMILY, FONT_URL, APPNEXUS, AMX, PUBMATIC, UNRULY, RUBICON, SONOBI, NOBID, CPMSTAR, INSTICATOR, YIELDMO, EPSILON, TRIPLELIFT, ONETAG, MINUTEMEDIA, SMAATO, SOVRN, IMPROVE_DIGITAL, EQUATIV, SHARETHROUGH, AD_GEO_BLOCK, SCROLL_TARGET, PRELOAD_COUNT } = process.env

function sellwildShortsController() {
  const elements = new Map<string, SellwildShortsElement>();

  return {
    isReady: null as null | Promise<void>,
    async initialize() {
      if (this.isReady) return this.isReady;

      this.isReady = loadDependencies();

      return this.isReady;
    },
    currentMuted: true,
    selectedMuted: false,
    async elementAdded (el: HTMLElement) {
      const element = el as SellwildShortsElement
      if (elements.get(el.id) === el) {
        return
      }

      elements.set(element.id, element)

      await this.isReady

      element.fullscreenOpen = false
      element.playVideo = (i) => {
        const selected = mainVideos[i]
        const el = selected && selected.parentElement as HTMLElement & { open: () => void }
        if (el && typeof el.open === 'function') {
          el.open()
        }
      }
      element.pauseVideo = () => {
        for (const video of mainVideos) {
          video.pause()
        }
      }
      let currentIndex = 0
      let lastAdPlayed: Date|null = null
      let currentListing: IListing|null = null
      
      const search = new URLSearchParams(window.location.search)
      
      let type = getAttributeAnyCasing(element, 'type') || TYPE || search.get('type') || ''
      if (type === 'verticalMobile') {
        type = isMobile() ? 'Vertical' : 'Carousel'
      }
      const customizationsCtrl = makeCustomizationController(element, search)
      const widgetName = customizationsCtrl.getValue('name', NAME || '')
      const slug = getAttributeAnyCasing(element, 'slug') || SLUG || search.get('slug') || ''
      const code = CODE || getAttributeAnyCasing(element, 'code') || search.get('code') || ''
      const iu = getAttributeAnyCasing(element, 'iu') || IU || search.get('iu')
      const iuInstream = getAttributeAnyCasing(element, 'iuInstream') || IU_INSTREAM || search.get('iuInstream')
      const adUnit = getAttributeAnyCasing(element, 'adUnit') || AD_UNIT || search.get('adUnit')
      const cacheLocal = getAttributeAnyCasing(element, 'videosLocal') || VIDEOS_LOCAL || search.get('videos-local')
      const keywordsEl = (document.querySelector('meta[name=keywords]') || document.querySelector('head>title')) as HTMLElement
      const keywordsContent = keywordsEl ? keywordsEl.getAttribute('content') || keywordsEl.textContent || '' : ''
      const keywords = keywordsContent.trim().slice(0, 100).replace(/[|]+/, '').split(',').map(v => v.trim()).join(',')
      const videoTagCacheCount = Number(getAttributeAnyCasing(element, 'videosTagCacheCount') || VIDEO_TAG_CACHE_COUNT || search.get('videosTagCacheCount')) || 0
      const videoTagCache = keywords.trim() && videoTagCacheCount ? `https://tbd4rmdvjk.execute-api.us-east-1.amazonaws.com/dev/playlist?keywords=${keywords.trim()}&count=${videoTagCacheCount}&v=1` : ''
      const cache = getAttributeAnyCasing(element, 'videos') || VIDEOS || search.get('videos')
      const filter = getAttributeAnyCasing(element, 'filter') || FILTER || search.get('filter')
      const cacheLocalVideos = await loadContent(cacheLocal)
      const cacheVideos = (await loadContent([videoTagCache, cache].filter(v => v).join('|')))
        .filter((v) => !v.resolution || v.resolution === '9x16')
        .filter((v) => isFilterMatch(v, filter))
      const videos = fixedShuffle(cacheLocalVideos.concat(cacheVideos))

      if (keywords) {
        jaccardSort(videos, keywords)
      }

      const sources = videos[currentIndex].sources
      const defaultSrc = sources && sources[0].src ? sources[0].src : 'https://widget.sellwild.com/blank.mp4'
      const mobile = isMobile()
      const isMobileChrome = mobile && (/Chrome/i).test(navigator.userAgent)

      const stylesheet = document.createElement('style')
      stylesheet.innerHTML = makeStyles({ 
        disableClipPathTransition: mobile
      })
      
      const adType = getAttributeAnyCasing(element, 'adType') || AD_TYPE || search.get('adType')
      const schainSid = getAttributeAnyCasing(element, 'schainSid') || SCHAIN_SID || search.get('schainSid')
      const s2sConfig = getAttributeAnyCasing(element, 's2sConfig') || S2S_CONFIG || search.get('s2sConfig')
      const adRefreshMax = Number(getAttributeAnyCasing(element, 'adRefreshMax')) || Number(AD_REFRESH_MAX) || Number(search.get('adRefreshMax'))
      const adRefreshInterval = Number(getAttributeAnyCasing(element, 'adRefreshInterval')) || Number(AD_REFRESH_INTERVAL) || Number(search.get('adRefreshInterval'))
      const adDisableDisplay = String(getAttributeAnyCasing(element, 'adDisableDisplay') || AD_DISABLE_DISPLAY || search.get('adDisableDisplay')) === 'true'
      const bannerZid = getAttributeAnyCasing(element, 'bannerZid') || BANNER_ZID || search.get('bannerZid')
      const bottomBannerZid = getAttributeAnyCasing(element, 'bottomBannerZid') || BOTTOM_BANNER_ZID || search.get('bottomBannerZid')
      const mobileBannerZid = getAttributeAnyCasing(element, 'mobileBannerZid') || MOBILE_BANNER_ZID || search.get('mobileBannerZid')
      const mobileZids = parseNums(getAttributeAnyCasing(element, 'mobileZid') || MOBILE_ZID || search.get('mobileZid') || '')
      const displayZids = parseNums(getAttributeAnyCasing(element, 'displayZid') || DISPLAY_ZID || search.get('displayZid') || '')
      const bidstreamStack = Number(getAttributeAnyCasing(element, 'bidstreamStack') || BIDSTREAM_STACK || search.get('bidstreamStack') || 0)
      const maxAds = Number(getAttributeAnyCasing(element, 'maxDisplayAds') || MAX_DISPLAY_ADS || search.get('maxDisplayAds')) || 2

      const title = getAttributeAnyCasing(element, 'title') || TITLE || search.get('title')
      const linkText = getAttributeAnyCasing(element, 'linkText') || LINK_TEXT || search.get('linkText')
      const linkHref = getAttributeAnyCasing(element, 'linkHref') || LINK_HREF || search.get('linkHref')
      const targetBlank = getAttributeAnyCasing(element, 'targetBlank') !== 'false'
      const base = getAttributeAnyCasing(element, 'baseUrl')
      const baseUrl = typeof base === 'string' ? base : 'https://sellwild.com'
      const css = getAttributeAnyCasing(element, 'css') || CSS || search.get('css') || ''
      const initial = getAttributeAnyCasing(element, 'initial') || INITIAL || search.get('initial') || ''
      const primaryColor = getAttributeAnyCasing(element, 'primaryColor') || PRIMARY_COLOR || search.get('primaryColor') || ''
      const audigent = Boolean(getAttributeAnyCasing(element, 'audigent') || AUDIGENT || search.get('audigent')) || false
      const fontFamily = getAttributeAnyCasing(element, 'fontFamily') || FONT_FAMILY || search.get('fontFamily') || ''
      const fontUrl = getAttributeAnyCasing(element, 'fontUrl') || FONT_URL || search.get('fontUrl') || ''
      const scrollTarget = getAttributeAnyCasing(element, 'scrollTarget') || SCROLL_TARGET || search.get('scrollTarget') || ''
      const preloadCountStr = getAttributeAnyCasing(element, 'preloadCount') || PRELOAD_COUNT || search.get('preloadCount')
      const preloadCount = preloadCountStr && !isNaN(Number(preloadCountStr)) ? Number(preloadCountStr) : 0

      const attributes = {
        widgetType: 'shorts',
        widgetName,
        type,
        code,
      }

      pushEvent({ event: 'view', attributes })

      element.addEventListener('click', (ev) => {
        if (!(ev.target instanceof HTMLElement)) return

        const element = ev.target.closest('a[href]:not([href=""])')
        if (!element) return
        
        const href = element.getAttribute('href')
        if (!href) return
        
        const event = element.getAttribute('data-event') || 'click'
        const action = element.getAttribute('data-event-action') || ''
        const label = element.getAttribute('data-event-label') || ''
        pushEvent({ event, action, label, attributes: { ...attributes, href } })
      })

      if (primaryColor) {
        element.style.setProperty('--blue', primaryColor)
      }

      const ads = generateAds({
        slug,
        adType,
        adRefreshMax,
        adRefreshInterval,
        bannerZid,
        bottomBannerZid,
        mobileBannerZid,
        mobileZids,
        displayZids,
        bidstreamStack,
        audigent,
        adDisableDisplay,
        schainSid,
        s2sConfig,
        appnexus: customizationsCtrl.getObject('appnexus', APPNEXUS),
        amx: customizationsCtrl.getObject('amx', AMX), 
        pubmatic: customizationsCtrl.getObject('pubmatic', PUBMATIC), 
        unruly: customizationsCtrl.getObject('unruly', UNRULY), 
        rubicon: customizationsCtrl.getObject('rubicon', RUBICON), 
        sonobi: customizationsCtrl.getObject('sonobi', SONOBI), 
        nobid: customizationsCtrl.getObject('nobid', NOBID), 
        cpmstar: customizationsCtrl.getObject('cpmstar', CPMSTAR), 
        insticator: customizationsCtrl.getObject('insticator', INSTICATOR),
        yieldmo: customizationsCtrl.getObject('yieldmo', YIELDMO),
        epsilon: customizationsCtrl.getObject('epsilon', EPSILON),
        triplelift: customizationsCtrl.getObject('triplelift', TRIPLELIFT),
        onetag: customizationsCtrl.getObject('onetag', ONETAG),
        minutemedia: customizationsCtrl.getObject('minutemedia', MINUTEMEDIA),
        smaato: customizationsCtrl.getObject('smaato', SMAATO),
        sovrn: customizationsCtrl.getObject('sovrn', SOVRN), 
        improveDigital: customizationsCtrl.getObject('improveDigital', IMPROVE_DIGITAL), 
        equativ: customizationsCtrl.getObject('equativ', EQUATIV), 
        sharethrough: customizationsCtrl.getObject('sharethrough', SHARETHROUGH), 
        adGeoBlock: customizationsCtrl.getObject('adGeoBlock', AD_GEO_BLOCK), 
      } as ICustomizations, {
        prefix: 'shorts-google-tag',
        onBidderDone: (bids) => {
          pushFirstAdViewed({ attributes })
        },
        onAuctionEnd: (winningBids) => {
          for (const bid of winningBids || []) {
            pushEvent({
              event: 'adWinningBid',
              label: bid.adUnitCode,
              action: bid.bidder,
              amount: bid.cpm,
              attributes 
            })
          }
        },
        onAdRenderSucceeded: (event) => {
          pushEvent({
            event: 'adRenderSucceeded',
            label: event.bid.adUnitCode,
            action: event.bid.bidder,
            amount: event.bid.cpm,
            attributes 
          })
        }
      })

      ads.initialize()
      ads.useAudigent();

      const count = isMobile() ? 10 : 25

      element.innerHTML = `
        ${type !== 'Vertical' && title ? `<div class="sellwild-shorts-title">
          <h4>${title}</h4>
          ${linkText ? `<a data-event-action="main-link" href="${linkHref || sellwildUrl(baseUrl, '', code)}" ${targetBlank ? `target="_blank"` : ''} rel="nofollow sponsored noopener">${linkText}</a>` : ''}
        </div>` : ''}
        <div class="sellwild-shorts-banner sellwild-shorts-banner-top">
        ${type === 'Vertical' ? ads.useGoogleDisplay([320, 50]) : !mobile ? ads.useGoogleDisplay([728,90]) || ads.useTopBanner() : ''}
        </div>
        <div class="sellwild-shorts-outer">
        <div class="sellwild-shorts-inner">
        <div class="sellwild-shorts-container" data-selector="shorts-container">
          ${videos.slice(0, count).map((video, i) => `
          <div class="sellwild-short">
            <div class="sellwild-short-videos">
              <video width="100%" height="100%" playsinline muted data-selector="video-main" data-index="${i}" ${type !== 'Vertical' && video.poster ? `poster="${video.poster.replace('1.jpg', '0.jpg')}"` : ''}>
              </video>
              <div class="sellwild-current-loader" data-selector="current-loader"></div>
            </div>
            ${'link' in video.user ? `<a data-event-action="sold-by" data-event-label="${video.user.id}" href="${video.user.link || sellwildUrl(baseUrl, `/s/${video.user.id}`, code)}" ${targetBlank ? `target="_blank"` : ''} rel="nofollow sponsored noopener">
              <p class="video-title">${video.title}</p>
              <p class="video-username">Sold by: ${video.user.username}</p>
              <p class="learn-more">Learn More ></p>
            </a>
            ` : ''}
            </div>
          ${iuInstream && (i == 1) && !mobile ? `<div class="sellwild-short" style="min-width: unset;">
            <div class="sellwild-short-videos" data-selector="instream-ad"></div>
          </div>` : ''}
          ${type !== 'Vertical' && (adType === 'Prebid' || displayZids && displayZids.length) && (i + 1) / maxAds <= 2 && (i + 1) % 2 === 0 ? `<div class="sellwild-shorts-display">${ads.useGoogleDisplay([300,250]) || ads.useBidstreamDisplay()}</div>` : ''}
          `).join('\n')}
        </div>
        </div>
        </div>

        <div class="sellwild-shorts-fullscreen ${initial ? 'open' : ''}" data-selector="fullscreen">
          <div class="action-buttons">
          <button class="close-fullscreen" data-selector="close-fullscreen">${closeIcon()}</button>
          <button class="like" data-selector="like">${heartIconOutlined()}</button>
          ${window.navigator && 'share' in window.navigator ? `<button class="share" data-selector="share">${shareIcon()}</button>` : ''}
          </div>
          ${videos.slice(0, count).map((video, i) => `<div class="sellwild-short">
            <video width="100%" height="100%" playsinline muted data-selector="fullscreen-main" data-index="${i}">
            </video>
            <div class="video-content"></div>
            <div class="sellwild-current-listing" data-selector="current-listing"></div>
            <div class="sellwild-current-loader" data-selector="current-loader"></div>
            <div class="sellwild-controls">
              <div class="progress">
                <progress class="progress-bar" value="0" min="0" max="100" data-selector="progress-bar"></progress>
                <div class="progress-ticks" data-selector="progress-ticks"></div>
                <input class="progress-bar-seek" data-selector="progress-bar-seek" value="0" min="0" max="100" type="range" />
              </div>
              <div class="actions">
                <div class="icon" data-selector="seek-prev">
                  ${nextIcon("rotate-180")}
                </div>
                <div class="icon" data-selector="play-pause" data-state="playing">
                  ${playIcon('height-30')}
                  ${pauseIcon()}
                </div>
                <div class="icon" data-selector="seek-next">
                  ${nextIcon()}
                </div>
                <div class="icon" data-selector="mute-unmute" data-state="muted">
                  ${muteIcon('height-30')}
                  ${unmuteIcon('height-30')}
                </div>
                <div class="timing" data-selector="timing"></div>
                <!--<div class="icon" data-selector="fullscreen" data-state="not-fullscreen">
                  ${fullscreenIcon()}
                  ${exitFullscreenIcon()}
                </div>-->
              </div>
            </div>
            <div class="sellwild-current-details" data-selector="current-details">
              <div class="sellwild-current-details-notch" data-action="close"></div>
              <div class="sellwild-current-details-container">
                <div class="sellwild-current-details-close" data-action="close">×</div>
                <div class="sellwild-current-details-image-container">
                  <img data-selector="image" />
                </div>
                <div class="sellwild-current-details-content-container">
                  <h1 data-selector="title"></h1>
                  <a class="sellwild-current-details-profile" data-selector="profile" target="_blank">
                    <img data-selector="avatar" />
                    <p data-selector="username"></p>
                    <div data-selector="badge" class="badge">
                      ${checkmark()}
                    </div>
                    <p data-selector="price" class="sellwild-current-details-content-price"></p>
                  </a>
                  <p data-selector="description"></p>
                </div>
              </div>
              <div class="sellwild-current-details-buttons-container">
                <a data-selector="details" target="_blank">See all details</a>
                <a data-selector="cart" target="_blank">Add to cart</a>
              </div>
            </div>
          </div>`).join('\n')}
          <div class="scroll-for-more" data-selector="scroll-for-more">
            <p>Scroll for more videos</p>
          </div>
          <div class="sellwild-shorts-ad" data-selector="ad">
            <div class="sellwild-current-loader" data-selector="current-loader"></div>
          </div>
          </div>
          <div class="sellwild-shorts-banner sellwild-shorts-banner-bottom">
            ${type === 'Vertical' ? ads.useGoogleDisplay([320, 50]) : !mobile ? ads.useGoogleDisplay([728,90]) || ads.useBottomBanner() : ''}
          </div>

          ${fontUrl && fontFamily ? `<link rel="preconnect" href="https://fonts.gstatic.com"><link href="${fontUrl}" rel="stylesheet">` : ""}
          <style>
          ${fontFamily ? `#${element.id} {font-family: ${fontFamily};}` : ""}
          ${css}
          </style>
      `

      element.appendChild(stylesheet)

      ads.appendScripts(document.body).then(v => {
        if (v && v.blocked) {
          pushEvent({ event: 'adsGeoBlocked', action: v.type, label: v.value, attributes })
        }
      });
      const thumbnailVideos = Array.from(element.querySelectorAll('[data-selector=video-thumbnail]')) as HTMLVideoElement[]
      const mainVideos = Array.from(element.querySelectorAll('[data-selector=video-main]')) as HTMLVideoElementWithHls[]
      const fullscreenContainer = element.querySelector('[data-selector=fullscreen]') as HTMLElement
      const fullscreenClose = element.querySelector('[data-selector=close-fullscreen]') as HTMLElement
      const likeButton = element.querySelector('[data-selector=like]') as HTMLElement
      const shareButton = element.querySelector('[data-selector=share]') as HTMLElement
      const instreamAdContainer = element.querySelector('[data-selector=instream-ad]') as HTMLLoadableDeferElement
      const adContainer = element.querySelector('[data-selector=ad]') as HTMLLoadableDeferElement
      const scrollForMore = element.querySelector('[data-selector=scroll-for-more]') as HTMLElement
      const adLoader = adContainer.querySelector('[data-selector=current-loader]') as HTMLElement
      
      const mainFullscreenVideos = Array.from(element.querySelectorAll('[data-selector=fullscreen-main]')) as HTMLVideoElementWithHls[]
      const thumbnailFullscreenVideos = Array.from(element.querySelectorAll('[data-selector=fullscreen-thumbnail]')) as HTMLVideoElement[]

      adContainer.loadThenPlay = () => {
        adContainer.style.display = 'none'
        const current = mainFullscreenVideos[currentIndex].parentElement as HTMLLoadableElement
        fullscreenContainer.style['overflowY'] = 'scroll'
        current.loadThenPlay()
      }
      adContainer.loadThenPlayDefered = (t = 0) => {
        clearTimeout(adContainer.loadThenPlayDeferedTimer)
        adContainer.loadThenPlayDeferedTimer = setTimeout(() => {
          adContainer.loadThenPlay()
        }, t) as any
      }
      adContainer.showLoader = () => {
        adLoader.classList.add('show')
        return adLoader
      }
      adContainer.hideLoader = () => {
        adLoader.classList.remove('show')
        return adLoader
      }
      adContainer.preventScroll = () => {
        if (!window.navigator.userAgent.match(/Mobile|Android|BlackBerry/)) {
          fullscreenContainer.style['overflowY'] = 'hidden'
        }
        return adLoader
      }
      

      
      if (scrollTarget) {
        const scrollPadding = Math.max(window.innerHeight, 100)
        const target = scrollTarget === 'window' ? window : document.querySelector(scrollTarget) as HTMLElement
        const initialParent = element.parentElement
        const getScrollTop = () => {
          if (target instanceof HTMLElement) {
            return target.scrollTop
          }

          return window.scrollY
        }
        
        if (target) {
          const moveElementToParent = debounce(() => {
            target.removeEventListener('scroll', leftBottom)
            target.addEventListener('scroll', reachedBottom)
            if (initialParent) {
              initialParent.append(element)
            }
            fullscreenClose.innerHTML = closeIcon()
            setTimeout(() => {
              element.classList.remove('bottom')
            }, 16);
          }, 200)
    
          const moveElementToBottom = debounce(() => {
            target.removeEventListener('scroll', reachedBottom)
            target.addEventListener('scroll', leftBottom)

            if (target instanceof HTMLElement) {
              target.appendChild(element)
            }
            setTimeout(() => {
              element.classList.add('bottom')
              fullscreenClose.innerHTML = arrowUpIcon()
            }, 16)
          }, 200)
    
          const leftBottom = () => {
            if (initialParent && getScrollTop() < window.innerHeight + scrollPadding) {
              target.removeEventListener('scroll', leftBottom)
              moveElementToParent()
            }
          }
          
          const reachedBottom = () => {
            if (getScrollTop() >= window.innerHeight + scrollPadding) {
              element.classList.add('bottom')
              target.removeEventListener('scroll', reachedBottom)
              moveElementToBottom()
            }
          }

          target.addEventListener('scroll', reachedBottom)
        }
      }

      document.addEventListener('visibilitychange', (ev) => {
        if (document.hidden) {
          // PAUSE ALL
          pauseAllVideo()
        } else {
          // RESUME
        }
      });

      function pauseAllVideo () {
        const ads = Array.from(element.querySelectorAll('sellwild-video')) as (HTMLElement & { adsManager: any })[]
        for (const ad of ads) {
          if (ad && ad.adsManager) {
            ad.adsManager.pause()
          }
        }
        
        for (const video of mainFullscreenVideos) {
          video.muted = true
        }
      }

      document.addEventListener('keyup', (e) => {
        if (e.key === "Escape") {
          element.closeFullscreen(e)
        }
      })

      fullscreenClose.addEventListener('click', (ev) => {
        ev.stopPropagation()
        element.closeFullscreen(ev)
      })

      function getCurrentId() {
        const video = videos[currentIndex];
        return video && (video.id || video.videoId || video.title);
      }
    
      function getCurrentTitle() {
        const video = videos[currentIndex];
        return video && video.title;
      }

      const likeProvider = makeLikeProvider({
        likeButton,
        getCurrentVideo: () => videos[currentIndex],
        onLiked: (id) => pushEvent({ event: 'like', label: getCurrentTitle(), attributes: { videoId: id, ...attributes } })
      })
      
      shareButton && shareButton.addEventListener('click', () => {
        try {
          const id = getCurrentId()
          const title = getCurrentTitle()
          if (id && title) {
            if (window.navigator && 'share' in window.navigator) {
              (window.navigator as any).share({
               title: title,
               url: `https://sellwild.com/tv?id=${id}`
             })
            }
          }
        } catch {}
      })
      
      element.closeFullscreen = function (_ev) {
        element.fullscreenOpen = false
        document.body.classList.remove('sellwild-shorts-open')
        fullscreenContainer.classList.remove('open')
        for (const video of mainFullscreenVideos) {
          video.pause()
        }

        const ad = adContainer.querySelector('sellwild-video') as (HTMLElement & { adsManager: any })
        if (ad && ad.adsManager) {
          ad.adsManager.pause()
        }

        const sellwildTvs = Array.from(document.querySelectorAll('sellwild-tv')) as (HTMLElement & { manualPause: boolean; play: () => Promise<void> })[]
        
        let playedTv = false
        for (const sellwildTv of sellwildTvs) {
          if (!sellwildTv.manualPause) {
            Promise.resolve().then(() => sellwildTv.play()).catch((e) => {
              console.log('sellwildTv.play() error:', e)
            })
            playedTv = true
          }
        }

        if (!playedTv && instreamAdContainer) {
          const instreamAd = instreamAdContainer && instreamAdContainer.querySelector('sellwild-video') as (HTMLElement & { adsManager: any })
          if (instreamAd && instreamAd.adsManager) {
            instreamAd.adsManager.resume()
          }
        }
      }

      for (const i in mainVideos) {
        const fullscreenVideoElement = mainFullscreenVideos[i]
        const videoElement = mainVideos[i]
        const video = videos[i]
        videoElement.currentTime = 0.5
        // updateSources(videoElement, video.sources, '')
        
        if (videoElement.parentElement) {
          const loader = videoElement.parentElement.querySelector('[data-selector=current-loader]') as Element
          const playClip = () => {
            updateSources(videoElement, video.sources, '')
            videoElement.play().catch((e) => {
              // Handle or ignore the AbortError gracefully
              if (e.name !== 'AbortError') {
                console.error('videoElement.play() error:', e);
              }
            });
          }
          
          const pauseClip = () => {
            loader.classList.remove('show')
            videoElement.pause()
          }
          videoElement.parentElement.addEventListener('mouseenter', playClip)
          // videoElement.parentElement.addEventListener('touchstart', (e) => {
            // e.preventDefault();
            // playClip()
          // })
          videoElement.parentElement.addEventListener('mouseleave', pauseClip);
          // videoElement.parentElement.addEventListener('touchend', pauseClip)

          (videoElement.parentElement as HTMLElement & { open: (ev: MouseEvent) => void }).open = (ev?: MouseEvent) => {
            element.fullscreenOpen = true
            document.body.classList.add('sellwild-shorts-open')
            fullscreenContainer.classList.add('open')

            const ad = adContainer.querySelector('sellwild-video') as (HTMLElement & { adsManager: any })
            if (ad && ad.adsManager) {
              ad.adsManager.resume()
            }

            if (instreamAdContainer) {
              const instreamAd = instreamAdContainer.querySelector('sellwild-video') as (HTMLElement & { adsManager: any })
              if (instreamAd && instreamAd.adsManager) {
                instreamAd.adsManager.pause()
              }
            }

            if (isMobile()) {
              for (const video of mainFullscreenVideos) {
                video.muted = ev && ev.isTrusted ? false : true
              }
            }

            const mainFullscreenVideo = mainFullscreenVideos[i]
            if (mainFullscreenVideo) {
              scrollIntoView(fullscreenContainer, mainFullscreenVideo)
              playCurrentFullscreenVideo()
            }

            const sellwildTvs = Array.from(document.querySelectorAll('sellwild-tv')) as (HTMLElement & { pause: () => void })[]
            for (const sellwildTv of sellwildTvs) {
              sellwildTv.pause()
            }
          }

          videoElement.parentElement.addEventListener('click', (ev) => {
            pushEvent({ event: 'open', attributes })
            const element = videoElement.parentElement as HTMLElement & { open: (ev: MouseEvent) => void }
            element.open(ev)
          })

          videoElement.addEventListener('waiting', () => {
            loader.classList.add('show')
          })
          videoElement.addEventListener('playing', () => {
            loader.classList.remove('show')
          })
        }

        
        const fullscreenVideoContainer = fullscreenVideoElement.parentElement as HTMLLoadableElement
        if (fullscreenVideoContainer) {
          const loader = fullscreenVideoContainer.querySelector('[data-selector=current-loader]') as HTMLElement & { timerId: any }
          const progressBar = fullscreenVideoContainer.querySelector('[data-selector=progress-bar]') as HTMLProgressElement
          const progressSeek = fullscreenVideoContainer.querySelector('[data-selector=progress-bar-seek]') as HTMLInputElement
          const progressTicks = fullscreenVideoContainer.querySelector('[data-selector=progress-ticks]') as HTMLElement

          const scrollToNextBtn = fullscreenVideoContainer.querySelector('[data-selector=scroll-to-next]') as HTMLElement
          const seekPrev = fullscreenVideoContainer.querySelector('[data-selector=seek-prev]') as HTMLElement
          const playPause = fullscreenVideoContainer.querySelector('[data-selector=play-pause]') as HTMLElement
          const seekNext = fullscreenVideoContainer.querySelector('[data-selector=seek-next]') as HTMLElement
          const muteUnmute = fullscreenVideoContainer.querySelector('[data-selector=mute-unmute]') as HTMLElement
          const timing = fullscreenVideoContainer.querySelector('[data-selector=timing]') as HTMLElement
          const fullscreen = fullscreenVideoContainer.querySelector('[data-selector=fullscreen]') as HTMLElement
          const currentListingElement = fullscreenVideoContainer.querySelector('[data-selector=current-listing]') as HTMLElement
          const currentDetails = fullscreenVideoContainer.querySelector('[data-selector=current-details]') as HTMLElement & { close: () => void; open: () => void }

          fullscreenVideoContainer.showLoader = () => {
            loader.classList.add('show')
            loader.style.bottom = '0'
            return loader
          }
          fullscreenVideoContainer.hideLoader = () => {
            loader.classList.remove('show')
            loader.style.bottom = ''
            return loader
          }
          fullscreenVideoContainer.loadThenPlay = () => {
            if (element.fullscreenOpen && currentIndex === Number(i)) {
              for (let j = 0; j < 3; j++) {
                const vid = videos[currentIndex + j]
                if (vid && vid.poster) {
                  const v = mainFullscreenVideos[currentIndex + j]
                  if (v && v.parentElement && !v.parentElement.querySelector('img.sellwild-poster')) {
                    const img = document.createElement('img')
                    img.classList.add('sellwild-poster')
                    img.src = vid.poster.replace('1.jpg', '0.jpg')
                    v.parentElement.querySelector('video')?.after(img)
                  }
                }
              }
              const cleanup = updateSources(fullscreenVideoElement, video.sources, '')
              for (let j = 0; j < preloadCount; j++) {
                if (mainFullscreenVideos[currentIndex + j + 1] && videos[currentIndex + j + 1]) {
                  preloadSources(mainFullscreenVideos[currentIndex + j + 1], videos[currentIndex + j + 1])
                }
              }
              likeProvider.updateCurrentLikeState()
              // fullscreenVideoElement.load()

              pushEvent({ event: 'play', label: video.title, attributes })
              fullscreenVideoElement.play().catch((err) => {
                console.log('fullscreenVideoElement.play() error:', err)
              })
            }
          }

          const updatePlayState = (video: HTMLVideoElement) => {
            playPause.setAttribute('data-state', video.paused ? 'paused' : 'playing')
          }
          
          const updateVolume = (video: HTMLVideoElement) => {
            muteUnmute.setAttribute('data-state', video.muted || video.volume === 0 ? 'muted' : 'unmuted')
          }

          const updateCurrentListing = (currentTime: number, duration: number) => {
            const progressValue = Math.floor(currentTime / duration * 100)
            progressBar.value = progressValue
            progressSeek.value = String(progressValue)

            if (video.user && 'type' in video.user && video.user.type === 'sponsored') {
              if (!currentListingElement.innerHTML) {
                currentListingElement.innerHTML = makeSponsoredSection(video, title)
              }
              return
            }
    
            for (const j in video.listings) {
              const listing = video.listings[j]
              if (
                typeof listing.startTimestamp === 'number' && 
                typeof listing.endTimestamp === 'number' && 
                listing.startTimestamp <= currentTime && 
                listing.endTimestamp > currentTime
              ) {
                if (currentIndex % videos.length === Number(i) && currentListing !== listing) {
                  currentListing = listing
                  currentListingElement.innerHTML = makeListingSection(listing, baseUrl, code, targetBlank)
                  updateCurrentDetails(listing)
                }
                return
              }
            }
          }

          const updateCurrentDetails = (listing: IListing) => {
            const image = currentDetails.querySelector('[data-selector=image]') as HTMLImageElement
            const avatar = currentDetails.querySelector('[data-selector=avatar]') as HTMLImageElement
            const title = currentDetails.querySelector('[data-selector=title]') as HTMLElement
            const profile = currentDetails.querySelector('[data-selector=profile]') as HTMLAnchorElement
            const username = currentDetails.querySelector('[data-selector=username]') as HTMLElement
            const badge = currentDetails.querySelector('[data-selector=badge]') as HTMLElement
            const price = currentDetails.querySelector('[data-selector=price]') as HTMLElement
            const description = currentDetails.querySelector('[data-selector=description]') as HTMLElement
            const details = currentDetails.querySelector('[data-selector=details]') as HTMLAnchorElement
            const cart = currentDetails.querySelector('[data-selector=cart]') as HTMLAnchorElement

            image.src = listing.src || ''
            image.alt = listing.title || ''
            if ('avatar' in video.user) {
              avatar.src = video.user.avatar
            }
            if ('username' in video.user) {
              avatar.alt = video.user.username
              username.innerHTML = video.user.username
            }
            title.innerHTML = listing.title || ''
            price.innerHTML = `$${listing.price || ''}`
            description.innerHTML = ''
            
            if ('id' in video.user) {
              profile.setAttribute('data-event-action', 'user')
              profile.setAttribute('data-event-label', video.user.id)
              profile.href = sellwildUrl(baseUrl, `/s/${video.user.id}`, code)
            }
            details.setAttribute('data-event-action', 'product')
            details.setAttribute('data-event-label', listing.title || listing.id || '')
            details.href = sellwildUrl(baseUrl, `/product/${listing.id}/${(listing.title || '').toLowerCase().replace(/ /g, '-')}`, code)
            cart.setAttribute('data-event-action', 'cart')
            cart.setAttribute('data-event-label', listing.title || listing.id || '')
            cart.href = sellwildUrl(baseUrl, `/checkout/${listing.id}`, code)
          }

          currentListingElement.addEventListener('click', (ev) => {
            ev.preventDefault()
            currentDetails.open()
          })

          currentDetails.open = () => {
            if (video && !('type' in video.user)) {
              currentDetails.classList.remove('slide-out')
              currentDetails.classList.add('slide-up')
            } else if (video.href) {
              pushEvent({ event: 'clickRss', action: 'rss', label: video.title, attributes: { ...attributes, href: video.href } })
              window.open(video.href)
            }
          }
          
          currentDetails.close = () => {
            if (currentDetails.classList.contains('slide-up')) {
              currentDetails.classList.remove('slide-up')
              currentDetails.classList.add('slide-out')
            }
          }

          currentDetails.addEventListener('click', (ev) => {
            if (ev.target instanceof HTMLElement) {
              const action = ev.target.getAttribute('data-action')
              if (action === 'close') {
                currentDetails.close()
              }
            }
          })

          const renderTicks = () => {
            const current = videos[i]
            for (const listing of current.listings || []) {
              if (listing.startTimestamp) {
                const element = document.createElement('div')
                const left = `${Math.floor(listing.startTimestamp / fullscreenVideoElement.duration * 100)}%`
                element.style.left = left
                progressTicks.appendChild(element)
              }
            }
          }

          if (fullscreenVideoElement.parentElement) {
            const content = fullscreenVideoElement.parentElement.querySelector('.video-content')
            if (content) {
              content.addEventListener('click', (ev) => {
                ev.stopPropagation()
                currentDetails.close()
                if (fullscreenVideoElement.paused) {
                  fullscreenVideoElement.play().catch((e) => {
                    console.log('fullscreenVideoElement.play() error:', e)
                  })
                } else {
                  fullscreenVideoElement.pause()
                }
              })
            }
          }

          playPause.addEventListener('click', (ev) => {
            ev.stopPropagation()
            if (fullscreenVideoElement.paused) {
              fullscreenVideoElement.play()
            } else {
              fullscreenVideoElement.pause()
            }
          })
    
          muteUnmute.addEventListener('click', (ev) => {
            ev.stopPropagation()
            this.currentMuted = !fullscreenVideoElement.muted
            this.selectedMuted = !fullscreenVideoElement.muted
    
            for (const video of mainFullscreenVideos) {
              video.muted = !video.muted
            }
          })

          seekNext.addEventListener('click', (ev) => {
            ev.stopPropagation()
            const listingIndex = (video.listings || []).findIndex((listing) => !!(listing.startTimestamp && listing.startTimestamp > fullscreenVideoElement.currentTime))
            const nextListing = (video.listings || [])[listingIndex]
            if (nextListing && typeof nextListing.startTimestamp === 'number') {
              fullscreenVideoElement.currentTime = nextListing.startTimestamp
              if (fullscreenVideoElement.duration) {
                const value = Math.floor(nextListing.startTimestamp / fullscreenVideoElement.duration * 100)
                progressBar.value = value
                progressSeek.value = String(value)
              }
            } else if (fullscreenVideoContainer.nextElementSibling) {
              const video = fullscreenVideoContainer.nextElementSibling.querySelector('[data-selector=fullscreen-main]') as HTMLElement
              if (video) scrollIntoView(fullscreenContainer, video)
            } else {
              const firstVideoElement = mainFullscreenVideos[0]
              scrollIntoView(fullscreenContainer, firstVideoElement)
            }
          })
    
          seekPrev.addEventListener('click', (ev) => {
            ev.stopPropagation()
            const listingIndex = findLastIndex(video.listings, (listing) => !!(listing.startTimestamp && listing.startTimestamp <= fullscreenVideoElement.currentTime))
            const prevListing = video.listings[listingIndex - 1]
            if (prevListing && typeof prevListing.startTimestamp === 'number') {
              fullscreenVideoElement.currentTime = prevListing.startTimestamp
              if (fullscreenVideoElement.duration) {
                const value = Math.floor(prevListing.startTimestamp / fullscreenVideoElement.duration * 100)
                progressBar.value = value
                progressSeek.value = String(value)
              }
            } else if (fullscreenVideoContainer.previousElementSibling) {
              const prevVideo = fullscreenVideoContainer.previousElementSibling.querySelector('[data-selector=fullscreen-main]') as HTMLElement
              if (prevVideo) scrollIntoView(fullscreenContainer, prevVideo)
            } else {
              const lastVideoElement = mainFullscreenVideos[mainFullscreenVideos.length - 1]
              scrollIntoView(fullscreenContainer, lastVideoElement)
            }
          })

          progressSeek.addEventListener('input', (ev) => {
            ev.stopPropagation()
            fullscreenVideoElement.currentTime = Number(progressSeek.value) / 100 * fullscreenVideoElement.duration
          })

          fullscreenVideoElement.addEventListener('play', () => {
            updatePlayState(fullscreenVideoElement)
          })

          fullscreenVideoElement.addEventListener('pause', () => {
            updatePlayState(fullscreenVideoElement)
            clearTimeout(loader.timerId)
          })

          fullscreenVideoElement.addEventListener('volumechange', () => {
            updateVolume(fullscreenVideoElement)
          });

          fullscreenVideoElement.addEventListener('waiting', () => {
            loader.timerId = setTimeout(() => {
              loader.classList.add('show')
            }, 600)
          })

          fullscreenVideoElement.addEventListener('playing', () => {
            loader.classList.remove('show')
            clearTimeout(loader.timerId)
            if (fullscreenVideoElement.parentElement) {
              const img = fullscreenVideoElement.parentElement.querySelector('img.sellwild-poster') as HTMLImageElement
              if (img) {
                img.style.pointerEvents = 'none'
                img.style.opacity = '0'
              }
            }
          })

          fullscreenVideoElement.addEventListener('timeupdate', () => {
            if (typeof fullscreenVideoElement.currentTime === 'number' && fullscreenVideoElement.duration) {
              updateCurrentListing(fullscreenVideoElement.currentTime, fullscreenVideoElement.duration)
            }
          })

          fullscreenVideoElement.addEventListener('ended', () => {
            const nextFullscreenVideo = mainFullscreenVideos[(Number(i) + 1) % videos.length]
            if (nextFullscreenVideo) {
              scrollIntoView(fullscreenContainer, nextFullscreenVideo)
            }
          })

          if (fullscreenVideoElement.duration) {
            renderTicks()
          } else {
            fullscreenVideoElement.onloadedmetadata = renderTicks
          }
        }
      }

      fullscreenContainer.addEventListener('scroll', () => {
        const fullscreenRect = fullscreenContainer.getBoundingClientRect()
        
        const current = mainFullscreenVideos.reduce((acc, video, i) => {
          const rect = video.getBoundingClientRect();
          const videoHeight = video.offsetHeight;
          const threshold = videoHeight / 2;

          if (fullscreenRect.top > rect.top - threshold) {
            return i
          }

          return acc
        }, 0);


        if (currentIndex !== current) {
          currentIndex = current
          currentListing = null
          // renderContent()
          playCurrentFullscreenVideo()
          scrollForMore.classList.add('scrolled')
        }
      })

      function scrollIntoView (container: HTMLElement, element: HTMLElement) {
        const containerRect = container.getBoundingClientRect()
        const elementRect = element.getBoundingClientRect()
        container.scrollTo(0, elementRect.top - containerRect.top + container.scrollTop)
      }
      

      const SECOND = 1000
      const canPlayAdvertisement = () => {
        if (!lastAdPlayed) return true
        if (lastAdPlayed.valueOf() < (Date.now() - 75 * SECOND)) return true
        return false
      }

      if (type !== 'Vertical' && instreamAdContainer) {
        instreamAdContainer.loadThenPlayDefered = (t = 0) => {
          clearTimeout(instreamAdContainer.loadThenPlayDeferedTimer)
          instreamAdContainer.loadThenPlayDeferedTimer = setTimeout(() => {
            instreamAdContainer.loadThenPlay()
          }, t) as any
        }
        instreamAdContainer.loadThenPlay = () => {
          if (instreamAdContainer && iuInstream && adUnit) {
            const sellwildVideo = document.createElement('sellwild-video') as SellwildVideoElement & { 
              pushEvent: (event: string) => void;
            }
            sellwildVideo.setAttribute('iu', iuInstream)
            sellwildVideo.setAttribute('ad-code', 'instream-video')
            sellwildVideo.setAttribute('ad-unit', adUnit)
            sellwildVideo.setAttribute('bids', JSON.stringify(ads.useBids([1, 1])))
            sellwildVideo.setAttribute('src', defaultSrc)

            sellwildVideo.pushEvent = (event: string) => {
              pushEvent({ event, attributes })
            }
            sellwildVideo.onStarted = () => {
              const ad = sellwildVideo.querySelector('[data-selector=ad]') as HTMLDivElement
              ad.style.position = 'relative'
              pushFirstAdViewed({ attributes })
              sellwildVideo.pushEvent('adStarted');
            }
            sellwildVideo.onAllAdsCompleted = () => {
              sellwildVideo.pushEvent('allAdsCompleted');
              instreamAdContainer.loadThenPlayDefered(30000); 
              sellwildVideo.remove();;
            }
            sellwildVideo.onSkipped = () => {
              sellwildVideo.pushEvent('adSkipped');
              instreamAdContainer.loadThenPlayDefered(30000); 
              sellwildVideo.remove();
            }
            sellwildVideo.onAdError = () => {
              sellwildVideo.pushEvent('adError');
              instreamAdContainer.loadThenPlayDefered(10000); 
              sellwildVideo.remove();
            }
            sellwildVideo.onNoBids = () => {
              sellwildVideo.pushEvent('adNoBids');
              instreamAdContainer.loadThenPlayDefered(10000); 
              sellwildVideo.remove();
            }
            sellwildVideo.onCompleted = () => {
              sellwildVideo.pushEvent('adCompleted');
              instreamAdContainer.loadThenPlayDefered(30000); 
              sellwildVideo.remove();
            }
            instreamAdContainer.prepend(sellwildVideo)
          }
        } 
  
        instreamAdContainer.loadThenPlay()
      }

      const playAdvertisement = () => {
        if (fullscreenContainer && iu && adUnit && !fullscreenContainer.querySelector('sellwild-video') && canPlayAdvertisement()) {
          lastAdPlayed = new Date()
          const sellwildVideo = document.createElement('sellwild-video') as SellwildVideoElement & { 
            pushEvent: (event: string) => void; 
            removeSoftly: () => void;
          }
          sellwildVideo.pushEvent = (event: string) => {
            pushEvent({ event, attributes })
          }
          sellwildVideo.removeSoftly = () => {
            sellwildVideo.style.opacity = '0'
            requestAnimationFrame(() => {
              sellwildVideo.remove()
            })
          }
          // adContainer.preventScroll()
          sellwildVideo.setAttribute('iu', iu)
          sellwildVideo.setAttribute('ad-unit', adUnit)
          sellwildVideo.setAttribute('bids', JSON.stringify(ads.useBids([1, 1])))
          sellwildVideo.setAttribute('src', defaultSrc)
          // sellwildVideo.setAttribute('onLoaded', "this.parentElement.preventScroll();")
          sellwildVideo.onStarted = () => {
            pushFirstAdViewed({ attributes })
            sellwildVideo.pushEvent('adStarted'); 
            adContainer.hideLoader();
          }
          sellwildVideo.onAllAdsCompleted = () => {
            sellwildVideo.pushEvent('allAdsCompleted'); 
            adContainer.loadThenPlay(); 
            sellwildVideo.removeSoftly();
          }
          sellwildVideo.onSkipped = () => {
            sellwildVideo.pushEvent('adSkipped'); 
            adContainer.loadThenPlay(); 
            sellwildVideo.removeSoftly();
          }
          sellwildVideo.onAdError = () => {
            sellwildVideo.pushEvent('adError'); 
            adContainer.loadThenPlay(); 
            sellwildVideo.removeSoftly();
          }
          sellwildVideo.onCompleted = () => {
            sellwildVideo.pushEvent('adCompleted'); 
            adContainer.loadThenPlay(); 
            sellwildVideo.removeSoftly();
          }
          sellwildVideo.onNoBids = () => {
            sellwildVideo.pushEvent('adNoBids'); 
            adContainer.loadThenPlay(); 
            sellwildVideo.removeSoftly();
          }
          adContainer.prepend(sellwildVideo)
          adContainer.showLoader()
          adContainer.style.display = 'block'
        } else {
          adContainer.loadThenPlayDefered(200)
        }
      }

      function playCurrentFullscreenVideo () {
        const currentAd = fullscreenContainer.querySelector('sellwild-video')
        mainFullscreenVideos.forEach((video, i) => {
          if (i === currentIndex % mainFullscreenVideos.length) {
            if (!currentAd) {
              playAdvertisement()
            }
            // updateSources(video, videos[i].sources, '')
            // video.load()
            // video.play().catch((err) => {
            //   console.log(err)
            // })
            // console.log('Play', video)
          } else {
            const img = video.parentElement?.querySelector('img.sellwild-poster') as HTMLImageElement
            if (img) {
              img.style.pointerEvents = 'auto'
              img.style.opacity = '1'
            }

            if (!video.paused) {
              video.pause()
              if ('cleanupHls' in video && typeof video.cleanupHls === 'function') {
                video.cleanupHls()
              }
            }
          }
        })
      }

      const container = document.querySelector('[data-selector=shorts-container]') as HTMLElement  
      function updateCarousel() {
        element.classList.add('central-carousel')
        const items = Array.from(container.children) as HTMLElement[]
        const positions = ['left-2', 'left-1', 'center', 'right-1', 'right-2'];
        const totalItems = items.length;
        
        items.forEach((item) => {
          item.classList.remove(...positions);
        });


        positions.forEach((position, index) => {
          const itemIndex = (currentIndex - 2 + index + totalItems) % totalItems;
          items[itemIndex].classList.add(position);
        });
      }

      if (type === 'Carousel') {
        updateCarousel()

        const p = container.parentElement
        if (p) {
          const prev = document.createElement('button')
          const next = document.createElement('button')
          prev.innerHTML = '⏴'
          next.innerHTML = '⏵'
          prev.classList.add('carousel-button', 'prev')
          next.classList.add('carousel-button', 'next')
          prev.addEventListener('click', () => {
            currentIndex = (currentIndex - 1) % videos.length
            updateCarousel()
          })
          next.addEventListener('click', () => {
            currentIndex = (currentIndex + 1) % videos.length
            updateCarousel()
          })
          p.appendChild(prev)
          p.appendChild(next)
        }
      }

      if (type === 'Vertical') {
        const target = scrollTarget ? document.querySelector(scrollTarget) as HTMLElement : undefined
        initializeVerticalVideo(element, attributes, target)

        const wrapper = element.parentElement
        if (wrapper) {
          const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
              if (entry.isIntersecting) {
                element.playVideo(0)
                // observer.unobserve(wrapper)
              } else {
                element.pauseVideo()
              }
            })
          }, {
            root: null,
            threshold: 0,
          })
          observer.observe(wrapper);
        }

      } else if (initial && !isNaN(Number(initial))) {
        element.playVideo(Number(initial))
      }
    },
    elementRemoved (el: HTMLElement) {
    }
  }
}

function makeSponsoredSection (video: IVideo, title?: string | null) {
  return `
    <div>
    <div class="sellwild-listing-details slide-up" style="--delay: 0.1s">
      ${title ? `<h6 class="shorts-listing-title">${title}</h6>` : ''}
      ${video.title ? `<p class="shorts-listing-title">${video.title}</p>` : ''}
      <div class="sellwild-listing-details-sub">
      ${video.href ? `<span class="listing-link" style="margin-left: 0;">${video.hrefText ? video.hrefText : 'Learn More >'}</span>` : ''}
      </div>
    </div>
    </div>
  `
}

function makeListingSection (listing: IListing, baseUrl: string, code?: string|null, targetBlank?: boolean) {
  if (!listing.id || !listing.title) return ''
  // <a href="${sellwildUrl(baseUrl, `/product/${listing.id}/${listing.title.toLowerCase().replace(/ /g, '-')}`, code)}" ${targetBlank ? `target="_blank"` : ''} rel="nofollow sponsored noopener">
  return `
  <div>
  <img src="${listing.src}" alt="${listing.title}" class="shorts-listing-img slide-up" />
  <div class="sellwild-listing-details slide-up" style="--delay: 0.1s">
  <div class="sellwild-listing-details-main"><p class="shorts-listing-title">${listing.title}</p></div>
  <div class="sellwild-listing-details-sub">
  <span class="shorts-listing-price">$${listing.price}</span>
  <span class="listing-link">Learn More ></span>
  </div>
  </div>
  </div>
  `
  // </a>
}

function makeSources (sources: ISource[], frame='') {
  return sources.map((source) => `<source src="${source.src}${frame}" type="${source.type}">`).join('\n')
}

function preloadSources (element: HTMLVideoElementWithHls, video: IVideo) {
  const Hls = (window as any)['Hls']
  if (video && video.sources) {
    const source = video.sources[0]
    if (source && source.type === 'application/x-mpegURL' && Hls.isSupported()) {
      if (element.hls) return element
      const hls = new Hls({})
      hls.loadSource(source.src);
      hls.attachMedia(element);
      element.hls = hls
      return element
    }
  }
}

async function loadDependencies() {
  await Promise.all([
    loadScript('https://cdnjs.cloudflare.com/ajax/libs/hls.js/0.5.14/hls.min.js')
  ]);
}

const sellwildShortsCtrl = sellwildShortsController();
sellwildShortsCtrl.initialize();
observeCustomElement(
  "sellwild-shorts",
  sellwildShortsCtrl.elementAdded.bind(sellwildShortsCtrl),
  sellwildShortsCtrl.elementRemoved.bind(sellwildShortsCtrl)
);

function findLastIndex<V = unknown>(arr: V[], callback: (v: V, i: number, vs: V[]) => boolean) {
  for (let i = arr.length - 1; i >= 0; i--) {
    if (callback(arr[i], i, arr)) return i;
  }
  return -1;
}

function debounce(fn: (...args: any[]) => any, n = 0) {
  let timerId: any
  return (...args: any[]) => {
    clearTimeout(timerId)
    timerId = setTimeout(() => {
      fn(...args)
    }, n)
  }
}

function isFilterMatch (v: IVideo, filter: string | null) {
  if (typeof filter !== 'string') return true

  const filterL = filter.toLowerCase()
  if ((v.title || '').toLowerCase().includes(filterL)) return true
  if (v.listings.some(l => (l.title || '').includes(filterL))) return true
  if (v.tags.some(tag => (tag || '').includes(filterL))) return true

  return false
}

function parseValue (value: string | null, defaultValue: any = {}) {
  if (!value) return defaultValue
  try {
    return JSON.parse(value)
  } catch (err) {
    return defaultValue
  }
}

function parseNums (value = '') {
  if (!value) return []
  return String(value || '').split(',').map(v => Number(v.trim()) || v)
}

async function loadContent (value: string | null): Promise<IVideo[]> {
  if (!value) return []

  if (value.indexOf('http') === 0) {
    const urls = value.split('|').map(v => v.trim()).filter(v => v)

    const contents = await Promise.all(
      urls.map((url) => fetch(url).then(appendViewerHeaders))
    )

    const map = new Map<string, IVideo>()

    for (const content of contents) {
      for (const video of content) {
        map.set(video.id || video.videoId, video)
      }
    }

    return Array.from(map).map(([_, value]) => value)
  }
  try {
    return JSON.parse(value)
  } catch (err) {
    return []
  }
}

function sellwildUrl (baseUrl: string, path: string, partnerCode?: string | null) {
  if (partnerCode) return `${baseUrl}${path}${path.includes('?') ? '&' : '?'}p=${partnerCode}&utm_source=${partnerCode}&utm_content=shorts`
  return `${baseUrl}${path}`
}

function initializeVerticalVideo (shorts: HTMLElement & { closeFullscreen: (ev: Event) => void }, attributes: IEventAttributes, scrollTarget?: HTMLElement) {
  if (shorts.parentElement) {
    shorts.parentElement.classList.add('sellwild-wrapper-vertical')
    shorts.parentElement.style.overflow = 'unset';
  }

  const wrapper = shorts.parentElement as HTMLElement
  const scroller = wrapper.querySelector('.sellwild-shorts-fullscreen') as HTMLElement
  const closer = scroller.querySelector('.action-buttons') as HTMLElement
  const videos = Array.from(shorts.querySelectorAll('[data-selector=fullscreen-main]')) as HTMLVideoElement[]

  if (closer.parentElement && closer.parentElement.parentElement) {
    closer.parentElement.parentElement.appendChild(closer)
  }

  shorts.style.setProperty('--height', '100vh')
  const viewMore = document.createElement('div')
  viewMore.classList.add('view-more-control')
  viewMore.innerHTML = '<p>Tap to view more</p>'
  scroller.appendChild(viewMore)

  // const iheart = iheartPlayer({
  //   onPlay: () => {
  //     for (const video of videos) {
  //       video.muted = true
  //     }
  //   } 
  // })

  shorts.closeFullscreen = (ev) => {
    ev.stopPropagation()
    
    window.requestAnimationFrame(() => {
      if (scrollTarget && shorts.classList.contains('bottom')) {
        pushEvent({ event: 'toTop', attributes })
        scrollTarget.scrollTop = 0
      } else {
        pushEvent({ event: 'close', attributes })
        document.body.classList.remove('noscroll')
        shorts.classList.remove('full')
        if (shorts.parentElement) {
          shorts.parentElement.style.overflow = 'unset';
        }
      }
      
      for (const video of videos) {
        if (!video.paused) {
          video.muted = true
        }
      }
    })

    // iheart.releaseAudio()
  }
  wrapper.addEventListener('click', (ev) => {
    ev.stopPropagation()
    if (!ev.target || !(ev.target instanceof HTMLElement)) return
    const controls = ev.target.closest('.sellwild-controls')
    if (controls) return
    window.requestAnimationFrame(() => {
      pushEvent({ event: 'open', attributes })
      document.body.classList.add('noscroll')
      shorts.classList.add('full')
      const mobile = isMobile()
      if (!mobile) {
        for (const video of videos) {
          video.muted = false
        }
  
        if (shorts.parentElement) {
          shorts.parentElement.style.overflow = 'unset';
        }
      }

      // iheart.claimAudio()
    })
  })
}


const makeStyles = ({ disableClipPathTransition = false }: {disableClipPathTransition?: boolean} = {}) => `sellwild-shorts {
  --rounded: 12px;
  --spacing: 8px;

  --shorts-height: 360px;

  --blue: #92a6c7;
  --white: #fff;
  --black: #000;
  --text-dark: #2d2d2d;
  --text-light: #9b9b9b;
  --text-link: var(--blue);

  display: block;
}

.sellwild-shorts-container {
  display: inline-flex;
  max-width: 100%;
  flex-wrap: nowrap;
  /* grid-auto-flow:column;   */
  /* grid-auto-columns: calc(var(--shorts-height) * 0.5625); */
  grid-gap: var(--spacing); 

  overflow-y: hidden;
  scroll-snap-type: x mandatory;
  scroll-snap-stop: always;
  overflow-x: scroll;
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
  scroll-behavior: smooth;
  isolation: isolate;
}

.sellwild-shorts-title {
  display: flex;
  align-items: baseline;
  margin-bottom: var(--spacing);
}

.sellwild-shorts-title h4 {
  font-weight: 700;
  color: var(--text-dark);
  font-size: calc(20 / 16 * 1rem);
  margin: 0;
}

.sellwild-shorts-title a {
  font-weight: 400;
  color: var(--text-dark);
  font-size: calc(1rem);
  text-decoration: underline;
  margin: 0;
  margin-left: var(--spacing);
}

.sellwild-shorts-banner {
  display: flex;
  align-items: center;
  justify-content: center;
}

.sellwild-shorts-banner-top>div:not(:empty) {
  margin-bottom: var(--spacing)!important;
}

.sellwild-shorts-banner-bottom>div:not(:empty) {
  margin-top: var(--spacing)!important;
}

.sellwild-wrapper-vertical .sellwild-shorts-banner {
  position: absolute;
  z-index: 999999;
  left: 0;
  right: 0;
  transition: opacity 1s ease-in 0.4s, visibility 0s linear;
  opacity: 1;
  visibility: visible;
}

.sellwild-wrapper-vertical .sellwild-shorts-banner-top {
  top: var(--spacing);
}
.sellwild-wrapper-vertical .sellwild-shorts-banner-bottom {
  bottom: var(--spacing);
}

sellwild-shorts.full .sellwild-shorts-banner {
  visibility: hidden;
  opacity: 0;
  transition: opacity 0.3s ease-in, visibility 0s linear 0.3s;
}

.sellwild-shorts-display {
  display: flex;
  align-items: center;
  justify-content: center;
  height: var(--shorts-height);
}

.sellwild-shorts-fullscreen {
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  opacity: 0;
  visibility: hidden;
  background-color: #fff;
  z-index: 99999;

  display: grid;
  grid-auto-flow: row;  
  /* grid-gap: var(--spacing);  */

  overflow-x: hidden;
  scroll-snap-type: y mandatory;
  /* scroll-snap-stop: always; */
  overflow-y: scroll;
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
  scroll-behavior: smooth;
  isolation: isolate;
}

.sellwild-shorts-fullscreen.open {
  opacity: 1;
  visibility: visible;
}

.sellwild-shorts-container::-webkit-scrollbar, .sellwild-shorts-fullscreen::-webkit-scrollbar {
  display: none; /* Hide scrollbar for Chrome, Safari and Opera */
}

.sellwild-short {
  position: relative;
  scroll-snap-align: start;
  min-width: calc(var(--shorts-height) * 0.5625);
}

.sellwild-short-videos {
  position: relative;
  height: var(--shorts-height);
  border-radius: var(--rounded);
  background-color: #eee;
  overflow: hidden;
  box-shadow: 5px 5px 5px -4px rgba(163,163,163,1);
}

.sellwild-short video {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  appearance: none;
}

.sellwild-short .video-content {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.sellwild-short .sellwild-poster {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.sellwild-shorts-container .sellwild-short video {
  cursor: pointer;
}

.sellwild-shorts-fullscreen .sellwild-short, .sellwild-shorts-fullscreen .sellwild-shorts-ad {
  height: 80vh;
  width: calc(80vh * 0.5625);
  margin: 10vh auto;
  box-sizing: border-box;
  scroll-snap-align: center;
  border-radius: var(--rounded);
  overflow: hidden;
}

.sellwild-shorts-fullscreen .sellwild-short {
  box-shadow: 5px 5px 5px -4px rgba(163,163,163,1);
  background-color: #eee;
}

.sellwild-shorts-fullscreen .sellwild-shorts-ad {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 3;
}

.sellwild-shorts-ad .sellwild-current-loader {
  bottom: 0;
}

.sellwild-shorts-ad sellwild-video,
.sellwild-short-videos sellwild-video {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  background-color: rgba(0,0,0,0.9);
}

/* CSS FOR INSTREAM SHORTS ADS */
.sellwild-short-videos sellwild-video,
.sellwild-short-videos sellwild-video div[data-selector=ad]>div {
  position: relative!important;
}

.sellwild-short-videos sellwild-video div[data-selector=ad]>div>div {
  height: 100%;
}

.sellwild-short-videos sellwild-video div[data-selector=ad] video {
  position: relative!important;
  width: initial!important;
  height: 100%!important;
}

.sellwild-shorts-ad sellwild-video iframe, .sellwild-short-videos sellwild-video iframe {
  position: absolute!important;
  top: 0;
}

/* END CSS FOR INSTREAM SHORTS ADS */

@media screen and (max-width: 540px) {
  @supports(height: 100dvh) {
    .sellwild-shorts-fullscreen .sellwild-short, .sellwild-shorts-fullscreen .sellwild-shorts-ad {
      height: 100dvh;
      width: 100%;
      margin: auto;
      border-radius: 0;
    }
  }
}

.sellwild-shorts-fullscreen .sellwild-short video {
  height: 100%;
  width: 100%;
}

.sellwild-short video:last-of-type {
  position: relative;
}

.sellwild-short a {
  color: #333;
  text-decoration: none;
}
.sellwild-short a p {
  margin: 0;
}
.sellwild-short .video-title {
  font-weight: 700;
  color: var(--text-dark);
  font-size: 16px;
  max-height: 32px;
  line-height: 1;
  margin: var(--spacing) 0;
  padding: 0;
  overflow: hidden;
}
.sellwild-short .video-price {
  font-weight: 700;
  color: var(--text-dark);
  font-size: 16px;
  line-height: 1;
  margin: var(--spacing) 0;
  padding: 0;
}
.sellwild-short .video-username {
  font-weight: 400;
  font-size: 12px;
  color: var(--text-light);
  line-height: 1;
  margin: var(--spacing) 0;
  padding: 0;
}
.sellwild-short .learn-more {
  font-weight: 400;
  color: var(--text-link);
  font-size: calc(12 / 16 * 1rem);
  text-decoration: underline;
  line-height: 1;
  margin: var(--spacing) 0;
}

.sellwild-controls {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  background-color: rgba(0, 0, 0, 0.4);
}

.sellwild-controls .progress {
  position: relative;
  overflow: visible;
  height: 10px;
  width: 100%;
  z-index: 1;
  margin: 0;
}

.sellwild-controls .progress-bar {
  appearance: none;
  position: absolute;
  content: '';
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.sellwild-controls .progress-bar::-webkit-progress-bar {
  background-color: #eee;
}

.sellwild-controls .progress-bar::-webkit-progress-value {
  background-color: var(--blue);
}

.sellwild-controls .progress-bar-seek {
  -webkit-appearance: none;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  appearance: none;
  background: none;
  margin: 0;
  border: none;
  box-shadow: none;
  border-radius: 0;
  padding: 0;
}

.sellwild-controls .progress-bar-seek::-webkit-slider-thumb {
  appearance: none;
  width: 20px;
  height: 20px;
  background-color: var(--blue);
  border: 2px solid #eee;
  border-radius: 10px;
}

.sellwild-controls .progress-ticks div {
  position: absolute;
  cursor: none;
  top: 0;
  width: 6px;
  height: 100%;
  background-color: #aaa;
}

.sellwild-controls .actions {
  display: flex;
}

.sellwild-controls .icon .rotate-180 {
  transform: rotateY(180deg);
}

.sellwild-controls .icon {
  width: 55px;
  height: 55px;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background-color: rgba(0, 0, 0, 0);
  transition: background-color 0.3s ease-in;
}

.sellwild-controls .icon:hover {
  background-color: rgba(0, 0, 0, 0.8);
}

.sellwild-controls .icon svg {
  width: 72%;
  height: 72%;
}

.sellwild-controls .icon svg.height-30 {
  width: 54%;
  height: 54%;
}

.sellwild-controls .icon svg[data-selector=play],
.sellwild-controls .icon svg[data-selector=pause],
.sellwild-controls .icon svg[data-selector=mute],
.sellwild-controls .icon svg[data-selector=unmute],
.sellwild-controls .icon svg[data-selector=enter-fullscreen],
.sellwild-controls .icon svg[data-selector=exit-fullscreen] {
  display: none;
}
.sellwild-controls .icon[data-state=paused] svg[data-selector=play],
.sellwild-controls .icon[data-state=playing] svg[data-selector=pause],
.sellwild-controls .icon[data-state=unmuted] svg[data-selector=unmute],
.sellwild-controls .icon[data-state=muted] svg[data-selector=mute],
.sellwild-controls .icon[data-state=not-fullscreen] svg[data-selector=enter-fullscreen],
.sellwild-controls .icon[data-state=is-fullscreen] svg[data-selector=exit-fullscreen] {
  display: block;
}

.sellwild-controls .timing {
  margin-right: auto;
}

.sellwild-current-loader {
  position: absolute;
  top: 0;
  bottom: 60px;
  left: 0;
  right: 0;
  margin: auto;
  display: flex!important;
  align-items: center;
  justify-content: center;
  visibility: hidden;
  opacity: 0;
  background-color: rgba(0, 0, 0, 0.3);
  transition: opacity 0.3s ease-in, visibility 0s linear 0.3s;
  pointer-events: none;
}

sellwild-shorts .sellwild-shorts-fullscreen .scroll-for-more {
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  grid-gap: 12px;
  background-color: rgba(0,0,0,.4);
  opacity: 0;
  visibility: hidden;
  transition: opacity 1s ease-in, visibility 0s linear 1s;
  pointer-events: none;
}

@keyframes swipe-up {
  0% {
    transform: translate3d(0, 0, 0);
  }
  100% {
    transform: translate3d(0, -50%, 0);
  }
}

sellwild-shorts.full .sellwild-shorts-fullscreen .scroll-for-more:not(.scrolled) {
  opacity: 1;
  visibility: visible;
  transition: opacity 1s ease-in, visibility 0s linear;
}

sellwild-shorts .sellwild-shorts-fullscreen .scroll-for-more p {
  margin: 0;
  color: #fff;
  font-size: 22px;
}

sellwild-shorts .sellwild-shorts-fullscreen .scroll-for-more::after {
  content: url("data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 448 512'><path fill='white' d='M128 40c0-22.1 17.9-40 40-40s40 17.9 40 40V188.2c8.5-7.6 19.7-12.2 32-12.2c20.6 0 38.2 13 45 31.2c8.8-9.3 21.2-15.2 35-15.2c25.3 0 46 19.5 47.9 44.3c8.5-7.7 19.8-12.3 32.1-12.3c26.5 0 48 21.5 48 48v48 16 48c0 70.7-57.3 128-128 128l-16 0H240l-.1 0h-5.2c-5 0-9.9-.3-14.7-1c-55.3-5.6-106.2-34-140-79L8 336c-13.3-17.7-9.7-42.7 8-56s42.7-9.7 56 8l56 74.7V40zM240 304c0-8.8-7.2-16-16-16s-16 7.2-16 16v96c0 8.8 7.2 16 16 16s16-7.2 16-16V304zm48-16c-8.8 0-16 7.2-16 16v96c0 8.8 7.2 16 16 16s16-7.2 16-16V304c0-8.8-7.2-16-16-16zm80 16c0-8.8-7.2-16-16-16s-16 7.2-16 16v96c0 8.8 7.2 16 16 16s16-7.2 16-16V304z'/></svg>");
  width: 40px;
  height: 40px;
  animation: swipe-up 1s ease-in-out infinite both;
}

.sellwild-short-videos .sellwild-current-loader {
  bottom: 0;
}

.sellwild-current-loader.show {
  visibility: visible;
  opacity: 1;
  transition: opacity 0.3s ease-in, visibility 0s linear;
}

.sellwild-current-loader:after {
  content: " ";
  display: block;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 6px solid #fff;
  border-color: #fff transparent #fff transparent;
  animation: sellwild-current-loader 1.2s linear infinite;
}

@keyframes sellwild-current-loader {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.sellwild-current-listing>:first-child {
  position: absolute;
  bottom: 65px;
  left: 0;
  width: 100%;
  display: flex;
  align-items: center;
  padding: var(--spacing);
  padding-top: calc(var(--spacing) * 4);
  box-sizing: border-box;
  text-decoration: none;
  cursor: pointer;
}

.sellwild-current-listing>:first-child:not(:empty) {
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.8));
}

.sellwild-current-listing .shorts-listing-img {
  width: 76px;
  height: 76px;
  object-fit: cover;
}

.sellwild-current-listing .listing-details,
.sellwild-current-listing .sellwild-listing-details {
  display: flex;
  flex: 1;
  flex-direction: column;
  align-self: stretch;
  margin-left: calc(var(--spacing) / 2);
}

.sellwild-current-listing .listing-details-main,
.sellwild-current-listing .sellwild-listing-details-main {
  width: 100%;
  display: flex;
  align-items: center;
  margin-bottom: auto;
}

.sellwild-current-listing .listing-details-sub,
.sellwild-current-listing .sellwild-listing-details-sub {
  width: 100%;
  display: flex;
  align-items: center;
}

.sellwild-current-listing .shorts-listing-title {
  line-height: 1.2em;
  height: 2.4em;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 16px;
  color: var(--white);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  margin: 0;
}

.sellwild-current-listing h6.shorts-listing-title {
  height: 1.2em;
  font-size: 22px;
  font-weight: bold;
  margin-bottom: 8px;
}

.sellwild-current-listing .shorts-listing-price {
  text-align: left;
  align-self: flex-end;
  background-color: var(--black);
  color: var(--white);
  padding: calc(var(--spacing) / 2) var(--spacing);
  display: block;
  font-size: 13px;
  margin-right: calc(var(--spacing) / 2);
}

.sellwild-current-listing .listing-link {
  margin-left: auto;
  text-align: right;
  white-space: nowrap;
  align-self: center;
  color: var(--white);
  font-size: 20px;
  text-decoration: underline;
}

.sellwild-current-details {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: var(--white);
  border-top-left-radius: calc(var(--spacing) * 1.5);
  border-top-right-radius: calc(var(--spacing) * 1.5);
  height: 80%;
  z-index: 2;
  transform: translateY(100%);
}

.sellwild-current-details-container {
  display: flex;
  flex-direction: column;
  padding: calc(var(--spacing) * 2);
  overflow-y: auto;
  overflow-x: hidden;
  height: 100%;
  padding-bottom: 120px;
}

.sellwild-current-details-notch {
  content: '';
  position: absolute;
  z-index: 2;
  top: var(--spacing);
  height: var(--spacing);
  width: calc(var(--spacing) * 4);
  border-radius: calc(var(--spacing) / 2);
  left: 0;
  right: 0;
  margin: auto;
  background-color: rgba(0, 0, 0, 0.2);
  cursor: pointer;
}

.sellwild-current-details-close {
  position: sticky;
  top: 0;
  z-index: 2;
  padding: calc(var(--spacing) * 1) calc(var(--spacing) * 2);
  margin-left: auto;
  margin-right: calc(var(--spacing) * -2);
  color: rgba(0, 0, 0, 0.8);
  font-size: 28px;
  cursor: pointer;
}

.sellwild-current-details-image-container {
  position: relative;
  width: 100%;
  padding-top: 75%;
  background-color: rgba(0, 0, 0, 0.1);
  border-radius: calc(var(--spacing) / 2);
  overflow: hidden;
}

.sellwild-current-details-image-container img {
  position: absolute;
  top: calc(var(--spacing) * 2);
  left: calc(var(--spacing) * 2);
  width: calc(100% - var(--spacing) * 4);
  height: calc(100% - var(--spacing) * 4);
  object-fit: contain;
  border-radius: calc(var(--spacing) / 2);
  overflow: hidden;
}

.sellwild-current-details-content-container {
  display: flex;
  flex-direction: column;
  grid-gap: calc(var(--spacing) * 2);
  padding: calc(var(--spacing) * 2) 0;
}

.sellwild-current-details-content-container h1 {
  color: var(--text-dark);
  font-size: 22px;
  font-weight: 400;
  line-height: 1.2;
  max-height: 2.4em;
  overflow: hidden;
  margin: 0;
}

.sellwild-current-details-content-container p {
  color: var(--text-dark);
  font-size: 16px;
  font-weight: 400;
  line-height: 1.2;
  max-height: 1em;
  margin: 0;
}

.sellwild-current-details-profile {
  display: flex;
  grid-gap: var(--spacing);
  align-items: center;
  width: 100%;
}

.sellwild-current-details-profile img {
  min-width: 32px;
  max-width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 2px solid #7a8e65;
  padding: 2px;
  object-fit: cover;
}

.sellwild-current-details-profile .badge {
  box-shadow: 0 2px 4px rgba(0,0,0,.5);
  cursor: pointer;
  min-width: 30px;
  height: 30px;
  background: #718cbb;
  border-radius: 100%;
  border: 1px solid #fff;
  justify-content: center;
  display: flex;
  align-items: center;
  justify-content: center;
}

.sellwild-current-details-profile .badge svg {
  height: 16px;
  width: 16px;
}

.sellwild-current-details-content-container .sellwild-current-details-content-price {
  font-size: 28px;
  margin-left: auto;
  color: var(--text-dark);
}

.sellwild-current-details-buttons-container {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  background-color: var(--white);
  border-top: 1px solid rgba(0, 0, 0, 0.2);
  padding: calc(var(--spacing) * 2);
  display: flex;
  grid-gap: var(--spacing);
  box-sizing: border-box;
}

.sellwild-current-details-buttons-container a {
  flex: 1;
  background-color: var(--white);
  border-radius: calc(var(--spacing) * 1);
  border: 1px solid rgba(0, 0, 0, 0.2);
  height: 34px;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-dark);
}

.sellwild-current-details-buttons-container a:first-child {
  border: 1px solid rgba(0, 0, 0, 0.2);
}

.sellwild-current-details-buttons-container a:last-child {
  background-color: #fed912;
  border: none;
}

.slide-up {
  animation: slide-up ease-in both;
  animation-duration: var(--duration, 0.3s);
  animation-delay: var(--delay, 0);
}

.slide-out {
  animation: slide-out ease-in both;
  animation-duration: var(--duration, 0.3s);
  animation-delay: var(--delay, 0);
}

@keyframes slide-up {
  0% {
    transform: translateY(100%);
    opacity: 0;
  }
  100% {
    transform: translateY(0%);
    opacity: 1;
  }
}

@keyframes slide-out {
  0% {
    transform: translateY(0%);
    opacity: 1;
  }
  100% {
    transform: translateY(100%);
    opacity: 0;
  }
}

.action-buttons {
  position: fixed;
  top: 12px;
  right: 12px;
  z-index: 3;
  display: flex;
  flex-direction: column;
  grid-gap: 24px;
}

/* .close-fullscreen { */
.action-buttons button {
  width: 44px;
  height: 44px;
  border-radius: 22px;
  font-size: 32px;
  border: none;
  outline: none;
  background-color: #aaa;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  margin: 0;
}

/* .close-fullscreen span { */
.action-buttons button span {
  line-height: 0;
  display: block;
}

.action-buttons button svg {
  width: 32px;
  height: 32px;
}

body.noscroll {
  overflow: hidden!important;
}
.sellwild-wrapper-vertical {
  margin: 0 -16px;
  min-height: 75vh;
  height: 75vh;
}

.sellwild-wrapper-vertical sellwild-shorts {
  display: block;
  height: 100%;
  
  ${disableClipPathTransition ? `
    transform: translate3d(0, 0, 0);
    overflow: hidden;
  ` : `
    clip-path: inset(0px 0px);
    transition: clip-path 1s ease-in-out;
    -webkit-transition: -webkit-clip-path 1s ease-in-out;
  `}
  position: relative;
  z-index: 999999;

  --blue: #c6002b;
  --height: 1000px;
}

.sellwild-wrapper-vertical sellwild-shorts.full {
  ${disableClipPathTransition ? `
    transform: unset;
  ` : `
  clip-path: inset(-100% -100%);
  -webkit-clip-path: inset(-100% -100%));
  `}
}

.sellwild-wrapper-vertical .sellwild-shorts-inner {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  text-align: center;
  overflow: auto;
}

.sellwild-wrapper-vertical .sellwild-shorts-fullscreen .sellwild-short, .sellwild-wrapper-vertical .sellwild-shorts-fullscreen .sellwild-shorts-ad {
  height: var(--height);
  transition: height 0.3s ease-in;
}

.sellwild-wrapper-vertical .sellwild-shorts-fullscreen .sellwild-shorts-ad {
  margin: 0 auto;
}

.sellwild-wrapper-vertical .sellwild-shorts-fullscreen {
  height: var(--height);
}

.sellwild-wrapper-vertical .sellwild-shorts-inner,
.sellwild-wrapper-vertical .sellwild-shorts-fullscreen {
  /* z-index: unset; */
  pointer-events: none;
  scroll-behavior: unset;
}

.sellwild-wrapper-vertical sellwild-shorts.full .sellwild-shorts-fullscreen .sellwild-short,
.sellwild-wrapper-vertical sellwild-shorts.full .sellwild-shorts-fullscreen .sellwild-shorts-ad {
  height: 100dvh;
 }
.sellwild-wrapper-vertical sellwild-shorts.full .sellwild-shorts-fullscreen {
  pointer-events: all;
  scroll-behavior: smooth;
  height: 100dvh;
}

@media screen and (min-width: 600px) {
  .sellwild-wrapper-vertical sellwild-shorts .sellwild-shorts-fullscreen .sellwild-short,
  .sellwild-wrapper-vertical sellwild-shorts .sellwild-shorts-fullscreen .sellwild-shorts-ad {
      height: 80dvh!important;
  }
}

.sellwild-wrapper-vertical .sellwild-shorts-container {
  /* display: block; */
  display: none;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  perspective: -200px;
  perspective-origin: top center;
}

.sellwild-wrapper-vertical .sellwild-shorts-container .sellwild-short {
  position: absolute;
  display: none;
  min-width: unset;
  overflow: hidden;
  clip-path: inset(0px);
  clip: rect(0px, auto, auto, 0px);
  /* animation: x var(--x-speed) linear infinite alternate; */
}

.sellwild-wrapper-vertical .sellwild-shorts-container .sellwild-short .sellwild-short-videos {
  width: 100%;
  height: 100%;
  transition: all 4s ease-in-out;
  /* animation: y var(--y-speed) linear infinite alternate; */
}

.sellwild-wrapper-vertical .view-more-control {
  position: fixed;
  pointer-events: none;
  top: 0;
  /* bottom: 0; */
  height: var(--height);
  /* max-height: 100dvh; */
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  grid-gap: 12px;
  background-color: rgba(0,0,0,.4);
  opacity: 1;
  visibility: visible;
  transition: opacity 1s ease-in, visibility 0s linear;
}

.sellwild-wrapper-vertical sellwild-shorts.full .view-more-control,
.sellwild-wrapper-vertical .view-more-control.hidden {
  opacity: 0;
  visibility: hidden;
  transition: opacity 1s ease-in, visibility 0s linear 1s;
}

.sellwild-wrapper-vertical .view-more-control p {
  margin: 0;
  color: #fff;
  font-size: 22px;
}

.sellwild-wrapper-vertical .view-more-control::after {
  content: url("data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 448 512'><path fill='white' d='M128 40c0-22.1 17.9-40 40-40s40 17.9 40 40V188.2c8.5-7.6 19.7-12.2 32-12.2c20.6 0 38.2 13 45 31.2c8.8-9.3 21.2-15.2 35-15.2c25.3 0 46 19.5 47.9 44.3c8.5-7.7 19.8-12.3 32.1-12.3c26.5 0 48 21.5 48 48v48 16 48c0 70.7-57.3 128-128 128l-16 0H240l-.1 0h-5.2c-5 0-9.9-.3-14.7-1c-55.3-5.6-106.2-34-140-79L8 336c-13.3-17.7-9.7-42.7 8-56s42.7-9.7 56 8l56 74.7V40zM240 304c0-8.8-7.2-16-16-16s-16 7.2-16 16v96c0 8.8 7.2 16 16 16s16-7.2 16-16V304zm48-16c-8.8 0-16 7.2-16 16v96c0 8.8 7.2 16 16 16s16-7.2 16-16V304c0-8.8-7.2-16-16-16zm80 16c0-8.8-7.2-16-16-16s-16 7.2-16 16v96c0 8.8 7.2 16 16 16s16-7.2 16-16V304z'/></svg>");
  width: 40px;
  height: 40px;
}

sellwild-shorts.bottom {
  position: relative;
}

sellwild-shorts.bottom .action-buttons {
  position: absolute;
  top: 82px;
}

.sellwild-wrapper-vertical sellwild-shorts .action-buttons, 
sellwild-shorts.bottom .action-buttons {
  z-index: 999999;
}

sellwild-shorts.bottom .sellwild-shorts-fullscreen .sellwild-shorts-ad {
  position: absolute;
}

sellwild-shorts.bottom .sellwild-shorts-outer {
  display: none;
}
sellwild-shorts.bottom .sellwild-shorts-fullscreen {
  position: relative;
  height: calc(100vh - 58px);
}

sellwild-shorts.bottom .sellwild-shorts-fullscreen .sellwild-short {
  height: calc(100vh - 58px);
}

sellwild-shorts .action-buttons button {
  background-color: var(--blue);
  height: 52px;
  width: 52px;
  border-radius: 26px;
  font-size: 38px;
  box-shadow: rgba(0,0,0,0.4) 7px 7px 7px;
}

.sellwild-wrapper-vertical sellwild-shorts .action-buttons button:active {
  box-shadow: rgba(0,0,0,0.4) 0px 0px 0px;
}

.sellwild-wrapper-vertical sellwild-shorts.full .sellwild-current-listing,
.sellwild-wrapper-vertical sellwild-shorts.full .sellwild-controls {
  opacity: 1;
  visibility: visible;
  transition: opacity 1s ease-in, visibility 0s linear;
}
.sellwild-wrapper-vertical sellwild-shorts:not(.full) .sellwild-current-listing,
.sellwild-wrapper-vertical sellwild-shorts:not(.full) .sellwild-controls {
  opacity: 0;
  visibility: hidden;
  transition: opacity 1s ease-in, visibility 0s linear 1s;
}


.sellwild-wrapper-vertical sellwild-shorts.full .action-buttons button {
  opacity: 1;
  transform: translate3d(0, 0, 0);
  visibility: visible;
  transition: opacity 1s ease-in, transform 0.4s ease-in, visibility 0s linear;
}
.sellwild-wrapper-vertical sellwild-shorts:not(.full) .action-buttons button {
  opacity: 0;
  transform: translate3d(0, -100%, 0);
  visibility: hidden;
  transition: opacity 1s ease-in, transform 0.4s ease-in, visibility 0s linear 1s;
}

@keyframes x {
  100% {
    transform: translateX(calc(100vw - var(--width)));
  }
}
@keyframes y {
  100% {
    transform: translateY(calc(100vh - var(--width)));
  }
}

.sellwild-wrapper-vertical .sellwild-current-details-container {
  padding-top: calc(var(--spacing) * 3);
}
.sellwild-wrapper-vertical .sellwild-current-details-close,
.sellwild-wrapper-vertical .sellwild-shorts-container .sellwild-short a {
  display: none;
}

.sw-particle {
  z-index: 999999;
  position: fixed;
  background-color: #fff;
  border-radius: 100%;
  width: 10px;
  height: 10px;
  pointer-events: none;
}

sellwild-shorts.central-carousel .sellwild-shorts-inner {
  position: relative;
  height: 400px;
}
sellwild-shorts.central-carousel .sellwild-shorts-container {
  position: absolute;
  left: 50%;
  /* top: 50%; */
  /* transform: translate(-50%, -50%); */
  perspective: 1000px;
  width: 100%;
  height: 100%;
  overflow: visible;
}
sellwild-shorts.central-carousel .sellwild-shorts-container .sellwild-short {
  position: absolute;
  opacity: 0;
  visibility: hidden;
  transition: transform 0.5s ease-in-out, opacity 0.5s ease-in-out, visibility 0s linear 0.5s, z-index 0s linear 0s;
}
sellwild-shorts.central-carousel .sellwild-shorts-container .sellwild-short.center {
  z-index: 5;
  opacity: 1;
  visibility: visible;
  transform: translateX(-50%) scale(1.2);
  transition: transform 0.5s ease-in-out, opacity 0.5s ease-in-out, visibility 0s linear 0s, z-index 0s linear 0s;
  
}
sellwild-shorts.central-carousel .sellwild-shorts-container .sellwild-short.left-1 {
  z-index: 4;
  opacity: 1;
  visibility: visible;
  transform: translateX(-130%) scale(0.9) translateY(30px);
  transition: transform 0.5s ease-in-out, opacity 0.5s ease-in-out, visibility 0s linear 0s, z-index 0s linear 0s;
}
sellwild-shorts.central-carousel .sellwild-shorts-container .sellwild-short.right-1 {
  z-index: 4;
  opacity: 1;
  visibility: visible;
  transform: translateX(30%) scale(0.9) translateY(30px);
  transition: transform 0.5s ease-in-out, opacity 0.5s ease-in-out, visibility 0s linear 0s, z-index 0s linear 0s;
}
sellwild-shorts.central-carousel .sellwild-shorts-container .sellwild-short.left-2 {
  z-index: 3;
  opacity: 1;
  visibility: visible;
  transform: translateX(-200%) scale(0.7) translateY(45px);
  transition: transform 0.5s ease-in-out, opacity 0.5s ease-in-out, visibility 0s linear 0s, z-index 0s linear 0s;
}
sellwild-shorts.central-carousel .sellwild-shorts-container .sellwild-short.right-2 {
  z-index: 3;
  opacity: 1;
  visibility: visible;
  transform: translateX(100%) scale(0.7) translateY(45px);
  transition: transform 0.5s ease-in-out, opacity 0.5s ease-in-out, visibility 0s linear 0s, z-index 0s linear 0s;
}
.carousel-button {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background-color: rgba(0, 0, 0, 0.4);
  color: white;
  border-radius: 100%;
  box-sizing: content-box;
  border: none;
  width: 1em;
  height: 1em;
  line-height: 1;
  padding: 6px;
  font-size: 20px;
  cursor: pointer;
}

.carousel-button.prev {
  left: 10px;
}

.carousel-button.next {
  right: 10px;
}`